import React, { useMemo } from 'react';
import { 
  Dialog, DialogTitle, DialogContent, DialogActions, 
  Button, Typography, Box, IconButton, Table, TableBody, 
  TableCell, TableHead, TableRow, ToggleButtonGroup, ToggleButton, 
  Stack, CircularProgress, Alert
} from '@mui/material';
import { Close, SwapHoriz, MergeType, CheckCircle } from '@mui/icons-material';
import { Contact } from '../types';
import { Tags } from './CRMTags';
import { OrgLinkChip } from './OrgLinkChip';
import { ContactMergeData } from '../data/useContactsMerge';
import { useContactAttributesConfig } from '../data/useContactAttributesConfig';
import { useCRMContext } from '../CRMContext';
import { DateDisplayWithDatetimeTooltip } from '../../schemed/TimeDisplay';

interface MergeContactsDialogProps {
  merge: ContactMergeData;
  onComplete?: () => void;
}

export const MergeContactsDialog = ({ merge, onComplete }: MergeContactsDialogProps) => {
  const {
    targetContact, sourceContact, selections, isMerging,
    isDialogVisible, cancelMerge, executeMerge, swapContacts, setFieldSelection
  } = merge;

  const { attributes: customAttributes } = useContactAttributesConfig();
  const ctx = useCRMContext();

  const fieldsConfig = useMemo(() => {
    const baseConfig = [
      { key: 'first_name', label: 'Имя' },
      { key: 'last_name', label: 'Фамилия' },
      { key: 'middle_name', label: 'Отчество' },
      { key: 'gender', label: 'Пол' },
      { key: 'birth_date', label: 'Дата рождения' },
      { key: 'consent_pd', label: 'Согласие ПД' },
      { key: 'consent_emails', label: 'Согласие на рассылки' },
      { key: 'communication_language', label: 'Язык коммуникации', collection: true, allowBoth: true },
      { key: 'geo_country', label: 'Страны', collection: true, allowBoth: true },
      { key: 'geo_city', label: 'Города', collection: true, allowBoth: true },
      { key: 'description', label: 'Описание', allowBoth: true },
      { key: 'categories', label: 'Категории', collection: true, allowBoth: true },
      { key: 'tags', label: 'Теги', collection: true, allowBoth: true },
      { key: 'phones', label: 'Телефоны', collection: true, allowBoth: true },
      { key: 'emails', label: 'Email', collection: true, allowBoth: true },
    ];

    const customConfig = customAttributes.map(attr => ({
      key: attr.code,
      label: `${attr.label} (Доп.)`,
      collection: false,
      allowBoth: attr.type !== 'bool' // Only texts get the "both" option
    }));

    return [...baseConfig, ...customConfig];
  }, [customAttributes]);

  if (!isDialogVisible || !targetContact || !sourceContact) return null;

  const renderCell = (field: string, contact: Contact, side: 'target' | 'source', isCollection = false) => {
    const isSelected = selections[field] === side || (isCollection && selections[field] === 'both');
    
    // Safely check nested attributes object or root level
    const rawValue = contact.attributes?.[field] ?? (contact as any)[field];
    
    let content: React.ReactNode = rawValue !== undefined && rawValue !== null 
      ? String(rawValue) 
      : <Typography variant="body2" color="textSecondary">---</Typography>;
    
    // Custom renderers
    if (field === 'tags') {
      content = contact.tags?.length ? <Tags tagIds={contact.tags} /> : '---';
    }
    // Renderer for simple string arrays like categories
    else if (field === 'categories') {
      const items = rawValue;
      content = items?.length ? (
        <Stack direction="row" flexWrap="wrap" gap={0.5}>
          {(items as string[]).map(c => ctx.settings.contact_categories?.find(cx => cx.code === c)?.label || c).map(category => (
            <OrgLinkChip
              key={category}
              organizationName={category}
            />
          ))}
        </Stack>
      ) : '---';
    }
    else if (field === 'geo_country' || field === 'geo_city' || field === 'communication_language') {
      const items = rawValue;
      content = items?.length ? (
        <Stack direction="row" flexWrap="wrap" gap={0.5}>
          {items.map((i: string, idx: number) => (
            <OrgLinkChip key={idx} organizationName={i} />
          ))}
        </Stack>
      ) : '---';
    }
    else if (field === 'emails' || field === 'phones') {
      const items = rawValue;
      content = items?.length ? (
        <Stack spacing={0.5}>
          {items.map((i: any, idx: number) => <Typography variant="body2" key={idx}>{i.value}</Typography>)}
        </Stack>
      ) : '---';
    }
    // Translation for consents and custom booleans
    else if (field === 'consent_pd' || field === 'consent_emails' || customAttributes.find(a => a.code === field && a.type === 'bool')) {
      if (rawValue === 'yes' || rawValue === true) content = 'Да';
      else if (rawValue === 'no' || rawValue === false) content = 'Нет';
      else if (rawValue === 'recalled') content = 'Отозвано';
    }

    return (
      <TableCell 
        onClick={() => setFieldSelection(field, side)}
        sx={{ 
          cursor: 'pointer', 
          bgcolor: isSelected ? 'primary.50' : 'background.paper',
          border: isSelected ? '1px solid' : '1px solid transparent',
          borderColor: isSelected ? 'primary.main' : 'divider',
          transition: 'all 0.2s',
          width: '40%',
          p: 1.5,
          position: 'relative',
          whiteSpace: 'pre-wrap', // Preserves \n\n for text concatenations
        }}
      >
        {isSelected && (
          <CheckCircle color="primary" sx={{ position: 'absolute', top: 4, right: 4, fontSize: 16, opacity: 0.5 }} />
        )}
        {content}
      </TableCell>
    );
  };

  return (
    <Dialog open={isDialogVisible} onClose={cancelMerge} maxWidth="md" fullWidth>
      <DialogTitle display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="h6" display="flex" alignItems="center" gap={1}>
          <MergeType color="primary" /> Слияние контактов
        </Typography>
        <IconButton onClick={cancelMerge} size="small" disabled={isMerging}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        <Alert severity="info" sx={{ m: 2 }}>
          Выберите значения полей, которые нужно сохранить. 
          Связи с компаниями и заметки будут объединены автоматически.
        </Alert>

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: '20%' }}>Поле</TableCell>
              <TableCell sx={{ width: '40%', bgcolor: 'background.default' }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="subtitle2" color="primary.dark">Целевой контакт</Typography>
                    <Typography variant="caption" sx={{ display: "block" }} color="textSecondary">ID: {targetContact._id}</Typography>
                    <Typography variant="caption" sx={{ display: "block" }} color="textSecondary">
                      Создан: <DateDisplayWithDatetimeTooltip field="created_at" row={targetContact} schema={{}} config={{ utcToLocal: true }} />
                    </Typography>
                    {!!targetContact.updated_at && <Typography variant="caption" sx={{ display: "block" }} color="textSecondary">
                      Изменен: <DateDisplayWithDatetimeTooltip field="updated_at" row={targetContact} schema={{}} config={{ utcToLocal: true }} />
                    </Typography>}
                  </Box>
                  <IconButton size="small" onClick={swapContacts} title="Поменять местами">
                    <SwapHoriz />
                  </IconButton>
                </Stack>
              </TableCell>
              <TableCell sx={{ width: '40%', bgcolor: 'background.default' }}>
                <Box>
                  <Typography variant="subtitle2" color="error.dark">Удаляемый контакт</Typography>
                  <Typography variant="caption" color="textSecondary" sx={{ display: "block" }}>ID: {sourceContact._id}</Typography>
                  <Typography variant="caption" sx={{ display: "block" }} color="textSecondary">
                      Создан: <DateDisplayWithDatetimeTooltip field="created_at" row={sourceContact} schema={{}} config={{ utcToLocal: true }} />
                    </Typography>
                    {!!sourceContact.updated_at && <Typography variant="caption" sx={{ display: "block" }} color="textSecondary">
                      Изменен: <DateDisplayWithDatetimeTooltip field="updated_at" row={sourceContact} schema={{}} config={{ utcToLocal: true }} />
                    </Typography>}
                </Box>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {fieldsConfig.map(({ key, label, collection, allowBoth }) => (
              <TableRow key={key}>
                <TableCell sx={{ fontWeight: 500, borderRight: '1px solid', borderColor: 'divider' }}>
                  {label}
                  <Box mt={1}>
                    <ToggleButtonGroup
                      size="small"
                      exclusive
                      value={selections[key]}
                      onChange={(_, val) => val && setFieldSelection(key, val)}
                      sx={{ '& .MuiToggleButton-root': { py: 0, px: 1, fontSize: '0.7rem', textTransform: 'none' } }}
                    >
                      <ToggleButton value="target">L</ToggleButton>
                      {allowBoth && <ToggleButton value="both">L+R</ToggleButton>}
                      <ToggleButton value="source">R</ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                </TableCell>
                {renderCell(key, targetContact, 'target', collection || allowBoth)}
                {renderCell(key, sourceContact, 'source', collection || allowBoth)}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={cancelMerge} color="inherit" disabled={isMerging}>
          Отмена
        </Button>
        <Button 
          onClick={async () => {
            await executeMerge();
            if (onComplete) onComplete();
          }} 
          variant="contained" 
          color="primary"
          disabled={isMerging}
          startIcon={isMerging ? <CircularProgress size={20} color="inherit" /> : <MergeType />}
        >
          {isMerging ? 'Выполнение...' : 'Объединить'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};